"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import { formatPublicDate } from "@/lib/public/format";

export type RecentTour = {
  slug: string;
  shipName: string;
  cruiseLineName: string;
  itineraryName: string;
  nights: number;
  departureAt: string;
  mediaId?: string | null;
};

const STORAGE_KEY = "cv-recent-tours";
const MAX_ITEMS = 3;
const MAX_AGE_MS = 30 * 86_400_000;

function readStore(): Array<RecentTour & { visitedAt: number }> {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) || "[]",
    );
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed
      .filter(
        (entry): entry is RecentTour & { visitedAt: number } =>
          typeof entry?.slug === "string" &&
          typeof entry?.visitedAt === "number" &&
          now - entry.visitedAt < MAX_AGE_MS,
      )
      .slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

/** Call inside the sailing detail page to register a visit (client cache). */
export function recordRecentTour(tour: RecentTour) {
  try {
    const entries = readStore().filter((entry) => entry.slug !== tour.slug);
    entries.unshift({ ...tour, visitedAt: Date.now() });
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(0, MAX_ITEMS)),
    );
  } catch {
    // Private mode / quota errors must never break the page.
  }
}

export function RecentlyViewedTracker({ tour }: { tour: RecentTour }) {
  useEffect(() => {
    recordRecentTour(tour);
    // Tour identity is stable per page; slug alone is a sufficient trigger.
  }, [tour.slug]);
  return null;
}

export function RecentlyViewed() {
  const [items, setItems] = useState<Array<RecentTour & { visitedAt: number }> | null>(
    null,
  );

  useEffect(() => {
    setItems(readStore());
  }, []);

  if (!items?.length) return null;

  return (
    <section className="recent-tours" aria-label="Son baktığınız turlar">
      <div className="public-container">
        <div className="section-heading">
          <div>
            <p className="public-eyebrow">Gezinmenize devam edin</p>
            <h2>Son Baktığınız Turlar</h2>
          </div>
        </div>
        <div className="cruise-grid">
          {items.map((item) => (
            <article className="cruise-card recent-tours__card" key={item.slug}>
              <Link
                className="recent-tours__link"
                href={`/cruise-seferleri/${item.slug}`}
                aria-label={`${item.shipName} seferini tekrar incele`}
              >
                <div className="cruise-card__visual" aria-hidden="true">
                  {item.mediaId ? (
                    <Image
                      src={`/api/media/${item.mediaId}`}
                      alt=""
                      fill
                      sizes="(max-width: 680px) 100vw, 25vw"
                      unoptimized
                    />
                  ) : (
                    <>
                      <span>{item.cruiseLineName.slice(0, 2).toUpperCase()}</span>
                      <div className="cruise-card__wave" />
                    </>
                  )}
                </div>
                <div className="cruise-card__content">
                  <p className="cruise-card__line">{item.cruiseLineName}</p>
                  <h3>{item.shipName}</h3>
                  <p className="cruise-card__date">
                    {formatPublicDate(new Date(item.departureAt))} · {item.nights} gece
                  </p>
                  <p className="recent-tours__itinerary">{item.itineraryName}</p>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
